/**
 * Per-channel orchestrator.
 *
 * Owns the four subsystems that make a single transcription channel
 * work end-to-end:
 *   1. AudioCaptureSource — `MediaStreamTrack` and `MediaStreamTrackProcessor`.
 *   2. AudioPcmPipeline   — frame → mono PCM16 with rate detection.
 *   3. ChannelTransport   — WebSocket + server-ready handshake + reconnect.
 *   4. ChannelDiagAccumulator — per-channel diagnostics counters.
 *
 * The channel also handles per-channel application logic that didn't
 * belong in any of the subsystems:
 *   - Speaker-offset bookkeeping across Deepgram-session reconnects
 *     (the legacy /transcribe-ws backend doesn't namespace speaker
 *     indices, so the client adds an offset on each reconnect to
 *     prevent collisions across sessions).
 *   - Decoding TranscriptionMessage payloads into a typed delegate
 *     surface (partial / final / segment-end / status / error) so
 *     the service doesn't have to switch on `msg.type`.
 *   - Emitting a one-shot first-frame deep dump for diagnostics.
 *
 * This class is dispose-once: a channel that needs a fresh capture
 * (e.g. AirPods reconnect, mic device switch, system loopback retry)
 * should be `.dispose()`-d and replaced with a brand-new instance.
 * That keeps the speaker-offset / pipeline-rate / transport-buffer
 * states cleanly correlated with the lifetime of the underlying
 * `MediaStream`.
 */
import * as Sentry from '@sentry/electron/renderer'
import { isAudioDebugEnabled } from '@shared/audio-debug'
import type { MicEpochVerdict } from '@shared/self-voice'
import { publishAudioDebugEvent } from './audio-debug'
import {
  ChannelDiagAccumulator,
  type ChannelDiagSnapshot,
  DIAG_DETAILED_FRAMES,
  DIAG_FIRST_FRAME_SAMPLES,
  hexDumpBytes,
  pcm16Samples,
} from './channel-diagnostics'
import { AudioPcmPipeline, floatToPcm16, type AudioPcmFrame } from './audio-pcm-pipeline'
import {
  AudioCaptureSource,
  type CaptureEndedReason,
  type CaptureSourceDelegate,
  type DebugCaptureStage,
} from './audio-capture-source'
import { ChannelTransport, type ChannelTransportDelegate } from './channel-transport'
import { PcmRingBuffer } from './pcm-ring-buffer'
import { TARGET_SAMPLE_RATE, type ChannelLabel, type TextSegment, type TranscriptionMessage } from './types'

/** How many seconds of PCM each channel keeps per debug ring buffer
 *  for `dumpDebugBundle()`. 30 s is enough to cover the typical "did
 *  Deepgram hear what I just said?" ask. Buffers exist only while
 *  audio-debug mode is enabled (Cmd+Shift+D overlay): the final tap
 *  costs ~1 MB (16 kHz mono int16 × 30 s) and the raw pre-resample
 *  tap ~3 MB at 48 kHz; both are freed when the mode turns off. */
const DEBUG_BUFFER_SECONDS = 30

/** Snapshot one debug ring buffer as a WAV stage. Duration and rate
 *  come from the buffer itself so the WAV-header arithmetic stays
 *  inside PcmRingBuffer. */
function recorderStage(recorder: PcmRingBuffer, name: string, description: string): DebugCaptureStage {
  return {
    name,
    description,
    wav: recorder.toWav(),
    sampleRate: recorder.sampleRate,
    durationSec: recorder.secondsBuffered(),
  }
}

/** What the channel needs from its owner to talk to the world. The
 *  service implements these methods on its own internal types so the
 *  channel never imports application-level state. */
export interface AudioChannelOwner {
  getAuthToken(): Promise<string>
  /** Build the WebSocket URL for this channel. Inputs (audioSessionId,
   *  locale) are owned by the service so a single locale change
   *  doesn't require touching every channel constructor. */
  buildWsUrl(channel: ChannelLabel): string
  /** True iff the service is in a state where a reconnect should be
   *  attempted (i.e. not idle, not stopping). */
  isReconnectAllowed(): boolean
  /** True iff host-channel mic frames should be zeroed right now, per the
   *  atomic `meeting-mic-gate` GlobalState (`shouldDropMic`). That decision
   *  already folds in self-mute AND the Google Meet "merged audio" override:
   *  it's true only when the user is muted on the call AND Meet is NOT routing
   *  their voice through a nearby device — in merged mode the laptop mic is the
   *  only capture path, so the gate stays open. When true the mic channel
   *  silences incoming frames before pipeline/WS so transcription doesn't fire
   *  on the user's keyboard/breath while muted, while still keeping the Deepgram
   *  session fed. Loopback is unaffected — only `isHost` channels honour this. */
  shouldDropMicFrames(): boolean
}

export interface AudioChannelDelegate {
  onPartial(source: number, text: string, timestamp: number): void
  /** A finalised transcript segment. The dispatcher re-derives the
   *  source-prefixed `dgSpeakerIndex` (`mic:N` / `sys:N`) from
   *  `seg.speaker`, so callers don't need to thread it through —
   *  `audio-channel` already strips the prefix into the bare digit
   *  inside `seg.speaker` for both backends. */
  onTranscript(seg: TextSegment): void
  onSegmentEnd(source: number, timestamp: number): void
  onEnergyUpdate(source: number, rms: number): void
  /** The PCM16 frame about to be sent, already silenced if the mic gate dropped it. */
  onPcm(source: number, pcm: ArrayBuffer): void
  /** WS connect succeeded after a previous disconnect. The service
   *  uses this to clear "reconnecting" status. */
  onConnectionOpen(channel: ChannelLabel): void
  /** WS disconnected (any reason). The service uses this to flip the
   *  status to 'reconnecting' if all channels are disconnected. */
  onConnectionDisconnect(channel: ChannelLabel): void
  /** A reconnect happened with the buffer overflowed — emit a gap
   *  marker so the user sees a visible "...partially not transcribed"
   *  marker in the transcript. */
  onGapMarker(channel: ChannelLabel, gapSeconds: number): void
  /** Audio-sessions DO signalled `speaker-namespace-bumped` for this
   *  source — pass through to the speaker resolver. */
  onSpeakerNamespaceBumped(source: 'mic' | 'sys', offset: number): void
  /** The capture source ended unexpectedly. The service decides
   *  whether to attempt a recovery (mic restart vs system restart). */
  onCaptureEnded(channel: ChannelLabel, reason: CaptureEndedReason): void
  /** A `Transcription session stopped` status arrived — the channel
   *  closed its WS to force a reconnect. The service can log /
   *  surface this if desired. */
  onUnexpectedSessionStop(channel: ChannelLabel, reason: string | undefined): void
  /** Backend reported an error message. */
  onBackendError(channel: ChannelLabel, message: string): void
  /** Backend WS message had a type we don't recognise. */
  onUnknownMessage(channel: ChannelLabel, msg: TranscriptionMessage): void
}

export class AudioChannel {
  readonly source: number
  readonly isHost: boolean
  readonly label: ChannelLabel

  private readonly capture: AudioCaptureSource
  private readonly transport: ChannelTransport
  private readonly pipeline: AudioPcmPipeline
  /** Rolling debug buffer of the exact PCM bytes shipped to
   *  Deepgram. Tapped after `pipeline.process` and before
   *  `transport.sendPcm` so the contents are byte-identical to what
   *  the wire sees. Surfaced through `dumpDebugBundle()` for audio
   *  runtime diagnostics; not used by transcription itself.
   *
   *  Allocated lazily on the first frame processed while audio-debug
   *  mode is enabled, and released when the mode turns off — zero
   *  memory / per-frame cost during normal operation. */
  private debugRecorder: PcmRingBuffer | null = null
  /** Rolling debug buffer of the pipeline's mono downmix BEFORE the
   *  resampler, at the device's native rate — A/B against `final` to
   *  isolate resampler artifacts from capture problems. Only populated
   *  when the input rate differs from TARGET_SAMPLE_RATE (otherwise
   *  it would duplicate `final` modulo PCM16 rounding). Same lazy
   *  audio-debug-gated lifecycle as `debugRecorder`. */
  private rawRecorder: PcmRingBuffer | null = null
  readonly diag = new ChannelDiagAccumulator()

  /** Added to raw Deepgram speaker indices so that speakers from a new
   *  Deepgram session after reconnect get fresh letter assignments. */
  private speakerIndexOffset = 0
  /** Tracks the highest raw Deepgram speaker index seen in the current
   *  WS session. On reconnect, speakerIndexOffset is bumped by
   *  maxSpeakerIndexSeen + 1. */
  private maxSpeakerIndexSeen = -1

  /** Cached log gate so the first AudioData format prints once per channel. */
  private formatLogged = false

  /** One-shot guard so a stream producing NaN/non-finite samples emits a
   *  single audio-debug event (and Sentry capture) per channel lifetime
   *  instead of flooding telemetry on every frame. NaN samples are zeroed
   *  by `floatToPcm16` so the wire stays valid, but the upstream cause
   *  (pipeline bug, resampler edge case, malformed AudioData) is exactly
   *  the kind of silent corruption we want visible to support. */
  private nanWarned = false

  /** True once dispose() begins. Guards every async resume so a
   *  late-firing capture/transport callback can't reach the service
   *  after teardown. */
  private disposed = false

  /** True iff the audio-track read loop should drop frames before
   *  pipeline processing. Mirrored to the capture source so the
   *  reader still drains the processor (no backpressure) but skips
   *  pipeline + WS work. */
  private paused = false

  constructor(
    config: { source: number; isHost: boolean; label: ChannelLabel; sessionStartTime: number },
    capture: AudioCaptureSource,
    private readonly owner: AudioChannelOwner,
    private readonly delegate: AudioChannelDelegate,
  ) {
    this.source = config.source
    this.isHost = config.isHost
    this.label = config.label
    this.capture = capture
    this.pipeline = new AudioPcmPipeline(config.label)

    const transportDelegate: ChannelTransportDelegate = {
      onWsConnect: (info) => {
        if (this.disposed) return
        if (info.isReconnect && !this.isHost) {
          // Bump speaker offset so the new Deepgram session's
          // speakers (0, 1, 2…) don't collide with the previous
          // session's speakers. Host-exempt: the mic is a single
          // speaker, so bumping it across reconnects is exactly what
          // split one person into "My mic - A/B/C" (HIG-5590).
          if (this.maxSpeakerIndexSeen >= 0) {
            this.speakerIndexOffset += this.maxSpeakerIndexSeen + 1
            console.log(
              `[AudioTranscriptionService] System channel reconnected — speaker offset now ${this.speakerIndexOffset}`,
            )
          }
          this.maxSpeakerIndexSeen = -1
        }
        // Emit the gap marker on ANY overflow — including a slow first
        // connect where the producer filled the buffer before the WS
        // ever opened. Gating on `isReconnect` would silently drop
        // bytes the user can hear missing from the transcript.
        if (info.bufferOverflowed && info.gapMs > 0) {
          this.delegate.onGapMarker(this.label, Math.round(info.gapMs / 1000))
        }
        this.delegate.onConnectionOpen(this.label)
      },
      onWsDisconnect: () => {
        if (this.disposed) return
        this.delegate.onConnectionDisconnect(this.label)
      },
      onWsMessage: (msg) => {
        if (this.disposed) return
        this.handleMessage(msg)
      },
      onServerReady: () => {
        // Bytes flushed by the transport on server-ready are already
        // accounted for via `onBytesSent`; nothing more to do here.
      },
      onBytesSent: (bytes) => this.diag.recordBytesSent(bytes),
      onBytesBuffered: (bytes) => this.diag.recordBytesBuffered(bytes),
      onWsMessageReceived: () => this.diag.recordWsMessage(),
      getWsUrl: () => this.owner.buildWsUrl(this.label),
      getAuthToken: () => this.owner.getAuthToken(),
      isReconnectAllowed: () => !this.disposed && this.owner.isReconnectAllowed(),
    }
    this.transport = new ChannelTransport(config.label, transportDelegate, config.sessionStartTime)
  }

  /** Acquire the capture source and connect the WS in parallel. PCM
   *  frames produced before the WS / server-ready arrive are buffered
   *  by the transport and flushed on `onServerReady`. Throws on
   *  capture acquisition failure (the caller handles surfacing the
   *  error to GlobalState); WS errors do not throw and are surfaced
   *  via the delegate / reconnect timer. */
  async start(): Promise<void> {
    if (this.disposed) {
      throw new Error(`Cannot start a disposed ${this.label} channel`)
    }
    const captureDelegate: CaptureSourceDelegate = {
      onFrame: (frame) => this.handleFrame(frame),
      onEnded: (reason) => this.handleCaptureEnded(reason),
      onError: (err) => this.handleCaptureError(err),
    }
    // Acquire capture FIRST. If acquisition fails (permission denied,
    // device unplugged, getDisplayMedia rejected) we want the throw
    // to propagate to the service's start path so it can fall back to
    // mic-only / surface a captureError. The transport.connect() is
    // fire-and-forget with its own error handling.
    await this.capture.start(captureDelegate)
    if (this.disposed) {
      this.capture.stop()
      throw new Error(`${this.label} channel was disposed during capture start`)
    }
    void this.transport.connect().catch((err) => {
      // `transport.connect()` resolves on WebSocket onerror and only throws
      // on a genuine programmer bug (e.g. URL-builder threw). Surface the
      // error: a swallowed throw here would mean the channel silently
      // never reconnects with no telemetry signal.
      console.error(
        `[AudioTranscriptionService] ${this.isHost ? 'Mic' : 'System'} WebSocket connect failed, will retry:`,
        err,
      )
      Sentry.captureException(err, {
        tags: {
          component: 'audio-transcription-service',
          channel: this.isHost ? 'mic' : 'system',
          errorId: 'audio-ws-connect-programmer-bug',
        },
      })
    })
  }

  private handleFrame(audioData: AudioData): void {
    if (this.disposed) {
      audioData.close()
      return
    }
    if (this.paused) {
      audioData.close()
      return
    }

    // While the user is self-muted on a Meet/Zoom/Teams call we must not
    // transcribe their keyboard/breath — but we also must not go silent on
    // the wire. Deepgram closes the socket (1011 / NET-0001) if it receives
    // no audio within its idle-timeout window, and the reconnect that
    // follows churns speaker indices and drops session context, repeated for
    // the entire muted stretch. So rather than dropping mic frames we run the
    // pipeline as usual (keeping the resampler + inter-frame timing
    // continuous) and zero the PCM payload below: Deepgram keeps counting
    // frames against real time and the transcript stays silent. Only the mic
    // (`isHost`) channel is gated; system loopback is untouched so the far
    // end keeps transcribing.
    //
    // EXCEPTION — Google Meet "merged audio" (Companion mode): when the laptop
    // is joined alongside a nearby room device, Meet routes the user's voice
    // through that device rather than the laptop mic. In that mode Google is
    // NOT transmitting the laptop audio, so if we also dropped the laptop mic
    // PCM on a self-mute the user's voice would be lost entirely (neither path
    // captures it). `shouldDropMicFrames()` already accounts for this — it's
    // false while merged — so the laptop mic keeps feeding transcription even
    // while the Meet mute button reads muted.
    const muted = this.isHost && this.owner.shouldDropMicFrames()

    const channelLabel = this.isHost ? 'Mic' : 'System'

    // One-time per-channel diagnostic: confirms what Chromium's
    // MediaStreamTrackProcessor is actually handing us. The track
    // settings dump only reflects the *requested* channel count; the
    // per-frame AudioData can disagree (e.g. a stereo track delivered
    // as mono on devices that downmix at the OS layer).
    if (!this.formatLogged) {
      this.formatLogged = true
      const frameDurationUs = (audioData.numberOfFrames / audioData.sampleRate) * 1_000_000
      console.log(
        `[AudioTranscriptionService] ${channelLabel} first AudioData frame: format=${audioData.format} sampleRate=${audioData.sampleRate} numberOfFrames=${audioData.numberOfFrames} numberOfChannels=${audioData.numberOfChannels} timestampUs=${audioData.timestamp} expectedDurationUs=${frameDurationUs.toFixed(0)}`,
      )
    }

    let frame: AudioPcmFrame
    try {
      frame = this.pipeline.process(audioData)
    } finally {
      audioData.close()
    }

    if (muted) {
      // Silence the freshly-allocated PCM16 buffer in place. `frame.pcm` is a
      // standalone ArrayBuffer minted by floatToPcm16 for this frame, so the
      // in-place zero can't corrupt any retained/shared state. Shipping
      // frame-sized silence (rather than dropping the frame) keeps Deepgram's
      // frame-count timing aligned with wall-clock while transcribing nothing.
      new Uint8Array(frame.pcm).fill(0)
      // Keep the raw debug tap honest too: while self-muted nothing may be
      // recorded anywhere, including the pre-resample buffer.
      frame.rawMono.fill(0)
    }

    // Per-frame timing diagnostics — log up to DIAG_DETAILED_FRAMES
    // lines so we have enough early-frame detail to diff
    // timestamp-vs-wall behaviour without flooding the log. With
    // Web Audio in front of the capture, large tsΔ-vs-wallΔ
    // disagreements would be the canonical signal that the
    // upstream source is mis-pacing samples (e.g. the macOS CATAP
    // cold-start rate bug we used to detect explicitly).
    if (frame.tsDeltaUs !== null && this.diag.detailedFramesLogged < DIAG_DETAILED_FRAMES) {
      this.diag.detailedFramesLogged += 1
      const frameDurationUs = (frame.numFrames / frame.inputRate) * 1_000_000
      console.log(
        `[AudioTranscriptionService:${this.label}] frame#${this.diag.totals.framesReceived + 1} ts=${frame.timestampUs}us tsΔ=${frame.tsDeltaUs}us wallΔ=${frame.wallDeltaMs?.toFixed(2) ?? 'n/a'}ms claimedDur=${frameDurationUs.toFixed(0)}us numFrames=${frame.numFrames} sampleRate=${frame.inputRate}`,
      )
    }

    this.diag.recordFrame(frame)
    // Report zero energy while muted so the mic meter falls immediately and
    // the meeting-end veto never sees stale loud values from the captured
    // (pre-silenced) frame.
    this.delegate.onEnergyUpdate(this.source, muted ? 0 : frame.energy)

    // First-occurrence NaN/non-finite surface. Samples themselves are
    // sanitized inside `floatToPcm16`, so the wire stays valid — but
    // a non-zero `nanCount` means *something upstream* (pipeline,
    // resampler, AudioData planes) is producing values it shouldn't.
    // Emit once per channel lifetime to flag the path without spamming.
    if (frame.nanCount > 0 && !this.nanWarned) {
      this.nanWarned = true
      const message = `pipeline produced ${frame.nanCount} NaN/non-finite sample(s) in a frame (sanitised before wire)`
      console.warn(`[AudioTranscriptionService:${this.label}] ${message}`)
      publishAudioDebugEvent({
        atMs: Date.now(),
        channel: this.label,
        severity: 'warn',
        message,
      })
      Sentry.captureException(new Error(`audio pipeline produced NaN samples (${this.label})`), {
        tags: {
          component: 'audio-transcription-service',
          channel: this.isHost ? 'mic' : 'system',
          errorId: 'audio-pipeline-nan-samples',
        },
        extra: { nanCount: frame.nanCount, inputRate: frame.inputRate, numFrames: frame.numFrames },
      })
    }

    if (!this.diag.firstFrameLogged) {
      this.diag.firstFrameLogged = true
      console.log(
        `[AudioTranscriptionService:${this.label}] first PCM chunk: bytes=${frame.pcm.byteLength} samples=${frame.pcm.byteLength / 2} sampleRate=${frame.inputRate}Hz peakAbs=${frame.peakAbs.toFixed(4)} rms=${frame.energy.toFixed(4)} hex="${hexDumpBytes(frame.pcm, DIAG_FIRST_FRAME_SAMPLES * 2)}" pcm16="${pcm16Samples(frame.pcm, DIAG_FIRST_FRAME_SAMPLES)}"`,
      )
    }

    if (isAudioDebugEnabled()) {
      this.recordDebugTaps(frame)
    }
    this.delegate.onPcm(this.source, frame.pcm)
    this.transport.sendPcm(frame.pcm)
  }

  /** Feed the debug ring buffers for one frame. Only called while
   *  audio-debug mode is on, so the buffers (and the extra PCM16
   *  conversion for the raw tap) cost nothing in normal operation.
   *  Buffers allocate lazily on the first gated frame. */
  private recordDebugTaps(frame: AudioPcmFrame): void {
    if (!this.debugRecorder) {
      this.debugRecorder = new PcmRingBuffer({ maxSeconds: DEBUG_BUFFER_SECONDS, sampleRate: TARGET_SAMPLE_RATE })
    }
    this.debugRecorder.append(frame.pcm)

    // Raw pre-resample tap. Skipped when the source already runs at the
    // target rate (the wrapped system channel, 16 kHz HFP mics): the
    // resampler is bypassed there, so raw would just duplicate `final`.
    // A mid-stream rate renegotiation (Bluetooth HFP ↔ A2DP) restarts
    // the tap, since one WAV cannot mix sample rates.
    if (frame.inputRate === TARGET_SAMPLE_RATE || frame.inputRate <= 0) {
      return
    }
    if (!this.rawRecorder || this.rawRecorder.sampleRate !== frame.inputRate) {
      this.rawRecorder = new PcmRingBuffer({ maxSeconds: DEBUG_BUFFER_SECONDS, sampleRate: frame.inputRate })
    }
    this.rawRecorder.append(floatToPcm16(frame.rawMono))
  }

  /** Snapshot every debug-tap stage in pipeline order: any source-
   *  owned pre-pipeline WAVs (no source exposes any today), the
   *  pre-resample `raw` tap when the source runs off the target rate,
   *  then the post-pipeline `final` WAV that's byte-identical to what
   *  Deepgram receives. Buffers exist only while audio-debug mode is
   *  enabled — before the first debug-mode frame, no recorder-backed
   *  stage is included at all. */
  dumpDebugBundle(): Array<DebugCaptureStage> {
    const stages = this.capture.dumpDebugCaptures()
    if (this.rawRecorder) {
      stages.push(
        recorderStage(
          this.rawRecorder,
          'raw',
          `Pre-resample mono downmix at the device's native rate (${this.rawRecorder.sampleRate} Hz), ` +
            `exactly as handed to the StreamingResampler. A/B against 'final' to isolate ` +
            `resampler artifacts from capture problems.`,
        ),
      )
    }
    if (this.debugRecorder) {
      stages.push(
        recorderStage(
          this.debugRecorder,
          'final',
          `Post-pipeline PCM16 mono at TARGET_SAMPLE_RATE (${TARGET_SAMPLE_RATE} Hz). ` +
            `Byte-identical to what is shipped over the WebSocket to Deepgram — this is the ` +
            `tap users typically want for "did Deepgram hear what I said?" verification.`,
        ),
      )
    }
    return stages
  }

  /** Audio duration currently held in the debug buffer, in seconds. */
  getRecordedSeconds(): number {
    return this.debugRecorder?.secondsBuffered() ?? 0
  }

  /** Drop the debug buffers' contents. Useful before reproducing a
   *  specific failure so the dump captures only the audio that
   *  triggered it, without any prior context bleeding in. Also clears
   *  the source-owned taps so all stages restart together. */
  clearDebugBuffer(): void {
    this.debugRecorder?.clear()
    this.rawRecorder?.clear()
    this.capture.clearDebugCaptures()
  }

  /** Free the debug ring buffers entirely. Called when audio-debug
   *  mode turns off so the multi-MB buffers (and any captured audio)
   *  don't outlive the debugging session. */
  releaseDebugBuffers(): void {
    this.debugRecorder = null
    this.rawRecorder = null
  }

  private handleCaptureEnded(reason: CaptureEndedReason): void {
    if (this.disposed) return
    this.delegate.onCaptureEnded(this.label, reason)
  }

  private handleCaptureError(err: unknown): void {
    if (this.disposed) return
    // Read-loop errors are treated as an unexpected capture end so the
    // service can decide whether to restart. The capture source already
    // logs the raw exception, but if a deterministic bug (e.g. pipeline
    // throwing on a degenerate frame) keeps firing, the service will
    // restart on every cycle — turning a real bug into a silent infinite
    // restart loop with no telemetry. Capture the err with channel +
    // stack so the loop becomes visible in Sentry.
    const errorObj = err instanceof Error ? err : new Error(`Capture read-loop error: ${String(err)}`)
    console.error(`[AudioTranscriptionService:${this.label}] capture read-loop error:`, err)
    Sentry.captureException(errorObj, {
      tags: {
        component: 'audio-transcription-service',
        channel: this.isHost ? 'mic' : 'system',
        errorId: 'audio-capture-readloop-error',
      },
    })
    this.delegate.onCaptureEnded(this.label, 'read-error')
  }

  private handleMessage(msg: TranscriptionMessage): void {
    const source = this.source
    const isHost = this.isHost
    const now = Date.now()

    switch (msg.type) {
      case 'partial': {
        // The audio-sessions DO anchors offsets to this client WS
        // connection, so offsetToAbsolute is enough; the legacy path
        // sends an absolute `timestamp` and that wins when present.
        const timestamp = msg.timestamp ?? (msg.offset != null ? this.offsetToAbsolute(msg.offset) : now)
        this.delegate.onPartial(source, msg.text || '', timestamp)
        break
      }

      case 'transcription':
      case 'final': {
        // Same timing contract as partials: DO offsets are relative to
        // this client WS connection, while the legacy path may send an
        // absolute timestamp.
        const endTime =
          msg.duration != null && msg.offset != null ? this.offsetToAbsolute(msg.offset + msg.duration) : now
        const startTime = msg.timestamp ?? (msg.offset != null ? this.offsetToAbsolute(msg.offset) : now - 1000)

        // Normalize speakers to a bare digit. DO messages already carry
        // a source-prefixed `dgSpeakerIndex`; legacy messages need the
        // client-side reconnect offset to avoid speaker collisions.
        const rawDgSpeaker = msg.speaker
        let adjustedSpeaker = rawDgSpeaker
        if (msg.dgSpeakerIndex) {
          const colonIdx = msg.dgSpeakerIndex.indexOf(':')
          adjustedSpeaker = colonIdx >= 0 ? msg.dgSpeakerIndex.slice(colonIdx + 1) : msg.dgSpeakerIndex
        } else {
          const speakerDigitMatch = rawDgSpeaker?.match(/(\d+)/)
          if (speakerDigitMatch) {
            const rawIdx = parseInt(speakerDigitMatch[1], 10)
            // Host never bumps, so tracking its max would only leave a stale counter.
            if (!this.isHost && rawIdx > this.maxSpeakerIndexSeen) {
              this.maxSpeakerIndexSeen = rawIdx
            }
            adjustedSpeaker = String(rawIdx + this.speakerIndexOffset)
          }
        }

        const seg: TextSegment = {
          text: msg.text || '',
          startTime,
          endTime,
          source,
          speaker: adjustedSpeaker,
          isHost,
          ...(isHost && msg.micEpoch != null ? { micEpoch: msg.micEpoch } : {}),
        }
        this.diag.recordTranscript()
        this.delegate.onTranscript(seg)
        break
      }

      case 'segment_end': {
        const ts = msg.timestamp || now
        this.delegate.onSegmentEnd(source, ts)
        break
      }

      case 'status': {
        const statusText = msg.status || msg.message || ''
        console.log(`[AudioTranscriptionService:${this.label}] Status:`, statusText)
        if (statusText === 'speaker-namespace-bumped') {
          // The DO owns timing for upstream Deepgram redials; the client
          // only forwards speaker-index namespace bumps to the resolver.
          const bumpedSource = msg.source === 'mic' ? 'mic' : msg.source === 'system' ? 'sys' : null
          if (bumpedSource && typeof msg.offset === 'number' && Number.isFinite(msg.offset)) {
            this.delegate.onSpeakerNamespaceBumped(bumpedSource, msg.offset)
          }
        }
        if (statusText.includes('Transcription session stopped') && this.owner.isReconnectAllowed()) {
          const reason = msg.reason ? ` reason=${msg.reason}` : ''
          const sourceTag = msg.source ? ` source=${msg.source}` : ''
          console.warn(
            `[AudioTranscriptionService:${this.label}] Unexpected session stop,${reason}${sourceTag} — reconnecting channel. Full message:`,
            JSON.stringify(msg),
          )
          this.delegate.onUnexpectedSessionStop(this.label, msg.reason)
          // Close the stale WebSocket so connectChannelWebSocket
          // won't bail out on a still-OPEN socket.
          this.transport.closeForReconnect('session-stopped')
        }
        break
      }

      case 'error': {
        const detail = msg.message || msg.error || 'Unknown error'
        const reason = msg.reason ? ` (reason: ${msg.reason})` : ''
        const errMsg = `[AudioTranscriptionService:${this.label}] Backend error: ${detail}${reason}`
        console.error(`${errMsg}. Full message:`, JSON.stringify(msg))
        Sentry.captureException(new Error(errMsg), {
          tags: {
            component: 'audio-transcription-service',
            channel: isHost ? 'mic' : 'system',
            errorId: 'audio-ws-backend-error',
          },
          extra: { message: msg },
        })
        this.delegate.onBackendError(this.label, detail)
        break
      }

      case 'connected': {
        console.log(`[AudioTranscriptionService:${this.label}] Session established`)
        break
      }

      default: {
        const warnMsg = `[AudioTranscriptionService:${this.label}] Unhandled WS message type "${msg.type}"`
        console.warn(`${warnMsg}: ${JSON.stringify(msg)}`)
        Sentry.captureException(new Error(warnMsg), {
          tags: {
            component: 'audio-transcription-service',
            channel: isHost ? 'mic' : 'system',
            errorId: 'audio-ws-unknown-message-type',
          },
          extra: { message: msg },
        })
        this.delegate.onUnknownMessage(this.label, msg)
      }
    }
  }

  /** Tells the audio session which mic voice is the user in one mic epoch. False when the WS isn't open. */
  sendSelfVoice(verdict: MicEpochVerdict): boolean {
    return this.transport.sendControl({ type: 'self-voice', ...verdict })
  }

  /** Send a force_segment control message. Returns true iff the WS was
   *  open at the moment of the call. The caller is responsible for
   *  awaiting the corresponding final transcript via the standard
   *  transcript delegate. */
  forceSegment(timestamp: number): boolean {
    return this.transport.sendControl({ type: 'force_segment', timestamp })
  }

  pause(): void {
    if (this.disposed) return
    this.paused = true
    this.capture.pause()
    this.pipeline.clearFrameTimings()
  }

  resume(): void {
    if (this.disposed) return
    this.paused = false
    this.capture.resume()
    this.pipeline.clearFrameTimings()
  }

  isWsOpen(): boolean {
    return this.transport.isWsOpen()
  }

  hasLiveTrack(): boolean {
    return this.capture.hasLiveTrack()
  }

  /** The primary capture audio track if one is currently held, else null.
   *  Forwarded from the capture source for diagnostic context (Sentry capture
   *  payloads). Null once the track has ended and been released. */
  getPrimaryTrack(): MediaStreamTrack | null {
    return this.capture.getPrimaryTrack()
  }

  /** Bytes currently held in the disconnect / pre-server-ready PCM
   *  buffer. The orchestrator polls this each energy tick to publish
   *  a UI-visible "buffered Xs" indicator during outages. */
  getBufferedBytes(): number {
    return this.transport.getBufferedBytes()
  }

  /** Full state snapshot for support-bundle / "Copy Debug Info" actions.
   *  Includes both the live transport view (WS readyState, server-ready,
   *  buffer bytes) and the cumulative reconnect bookkeeping (attempts,
   *  back-off delay, disconnect anchor, WS session start) plus the
   *  channel-level speaker-offset state. */
  inspectDebug(): {
    hasLiveTrack: boolean
    wsReadyState: number | null
    serverReady: boolean
    pcmBufferBytes: number
    reconnectAttempts: number
    reconnectDelayMs: number
    disconnectedAt: number | null
    wsSessionStartedAt: number
    speakerIndexOffset: number
    maxSpeakerIndexSeen: number
  } {
    const t = this.transport.inspectDebug()
    return {
      hasLiveTrack: this.capture.hasLiveTrack(),
      wsReadyState: t.wsReadyState,
      serverReady: t.serverReady,
      pcmBufferBytes: t.pcmBufferBytes,
      reconnectAttempts: t.reconnectAttempts,
      reconnectDelayMs: t.reconnectDelayMs,
      disconnectedAt: t.disconnectedAt,
      wsSessionStartedAt: t.wsSessionStartedAt,
      speakerIndexOffset: this.speakerIndexOffset,
      maxSpeakerIndexSeen: this.maxSpeakerIndexSeen,
    }
  }

  /** Snapshot provider for the diagnostics reporter. Defined as an
   *  arrow-function field so `channel.snapshot` is a stable reference
   *  across calls — the reporter's add/remove pair relies on object
   *  identity, and a `snapshot.bind(this)` would return a fresh
   *  function each time, leaking providers across capture restarts. */
  readonly snapshot = (): ChannelDiagSnapshot => {
    const transport = this.transport.inspect()
    const capture = this.capture.inspect()
    return {
      label: this.label,
      diag: this.diag,
      wsReadyState: transport.wsReadyState,
      serverReady: transport.serverReady,
      liveTrack: capture.liveTrack,
      trackInfo: capture.trackInfo,
      pcmBufferBytes: transport.pcmBufferBytes,
      claimedSampleRate: capture.claimedSampleRate,
    }
  }

  /** Final teardown. Order matters: dispose the transport BEFORE
   *  stopping capture so any in-flight reader cancellation / read-
   *  loop wind-down can't dispatch a frame to a half-disposed
   *  transport. */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.transport.dispose()
    this.capture.stop()
  }

  /** Convert a backend offset relative to this client WS connection into
   *  an absolute wall-clock timestamp. The DO and transport use the same
   *  connect-time anchor, so no extra client-side rebase is needed. */
  private offsetToAbsolute(offsetMs: number): number {
    return this.transport.getSessionStartedAt() + offsetMs
  }
}
